import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { PlatformInformationService } from '../../../../../projects/platform-manager/src/lib/services/platform-information.service';
import { Observable } from 'rxjs';
import { BookingsResponseInterface } from '../interfaces/bookings-response-interface';
import { map } from 'rxjs/operators';
import { CustomerValidateResponseInterface } from '../interfaces/customer-validate-response.interface';
import { CustomerToken } from '../interfaces/customer-token.interface';
import { TranslateService } from '@ngx-translate/core';
import { ContactInterface } from '../interfaces/contact.interface';
import { PersonalSettingsInterface } from '../interfaces/personal-settings-interface';
import { BookingsInterface } from '../interfaces/bookings-interface';

@Injectable({
  providedIn: 'root'
})
export class ApiService {
  private noCacheHeaders = new HttpHeaders({ Accept: 'application/json', 'Cache-control': 'no-cache' });
  private authHeaders = new HttpHeaders({
    Accept: 'application/json',
    'Cache-control': 'no-cache',
    'Use-Authorization': '1'
  });

  constructor(
    private client: HttpClient,
    private platformService: PlatformInformationService,
    private translateService: TranslateService
  ) {}

  /**
   * Returns all bookings a user has made
   */
  getBookingsPerUser(): Observable<BookingsResponseInterface> {
    const url = this.platformService.getApiEndpointFor('getBookingsPerUser');
    return this.client.get<BookingsResponseInterface>(url, { headers: this.authHeaders }).pipe(
      map(response => {
        response.bookings?.forEach(booking => {
          booking.end = new Date(booking.endDate);
          booking.start = new Date(booking.startDate);
        });
        response.bookings = response.bookings ? response.bookings : [];
        return response;
      })
    );
  }

  requestNewToken(email: string): Observable<{ message: string }> {
    const url = this.platformService
      .getApiEndpointFor('postRequestNewTokenApi')
      .replace('%lang%', this.translateService.currentLang);
    return this.client.post<{ message: string }>(url, { email: email }, { headers: this.noCacheHeaders });
  }

  validateCustomerToken(token: string): Observable<CustomerToken | null> {
    const url = this.platformService.getApiEndpointFor('getValidateCustomerTokenApi');
    return this.client
      .get<CustomerValidateResponseInterface>(url, {
        headers: this.noCacheHeaders,
        params: {
          token: token
        }
      })
      .pipe(
        map(response => {
          return {
            id: response.id,
            email: response.email,
            createdAt: new Date(response.createdAt),
            lastLogin: new Date(response.lastLogin),
            token: token
          };
        })
      );
  }

  logoutEverywhere() {
    const url = this.platformService.getApiEndpointFor('postLogoutEverywhere');
    return this.client.post(
      url,
      {},
      {
        headers: this.authHeaders
      }
    );
  }

  getUserContacts(customer: CustomerToken): Observable<Array<ContactInterface>> {
    const url = this.platformService
      .getApiEndpointFor('getContactsForUsersApi')
      .replace('%userId%', customer.id);
    return this.client.get<Array<ContactInterface>>(url, { headers: this.authHeaders }).pipe(
      map(response => {
        try {
          if (response.length) {
            response.forEach(contact => {
              contact.birthdayDate = new Date(contact.birthdate);
            });
          }
          return response;
        } catch (e) {
          console.error(e);
        }
      })
    );
  }

  getUserSettings(customer: CustomerToken): Observable<PersonalSettingsInterface> {
    const url = this.platformService
      .getApiEndpointFor('getCustomerAccountSettingsForUsersApi')
      .replace('%userId%', customer.id);
    return this.client.get<PersonalSettingsInterface>(url, { headers: this.authHeaders }).pipe(
      map(response => {
        return response;
      })
    );
  }

  saveUserSettings(
    personalSettings: PersonalSettingsInterface,
    token: CustomerToken
  ): Observable<PersonalSettingsInterface> {
    const user = {
      firstname: personalSettings.firstname,
      lastname: personalSettings.lastname,
      country: personalSettings.country,
      phone: personalSettings.phone,
      email: personalSettings.email,
      authUser: `/api/customer-account/users/${token.id}`
    };
    const requestSettings = { headers: this.authHeaders };

    if (!personalSettings.id) {
      return this.client
        .post<PersonalSettingsInterface>(
          this.platformService.getApiEndpointFor('createCustomerAccountSettings'),
          user,
          requestSettings
        )
        .pipe(
          map(response => {
            return response;
          })
        );
    }

    return this.client
      .put<PersonalSettingsInterface>(
        this.platformService
          .getApiEndpointFor('updateCustomerAccountSettings')
          .replace('%settingId%', personalSettings.id),
        user,
        requestSettings
      )
      .pipe(
        map(response => {
          return response;
        })
      );
  }

  deleteUserSettings(personalSettings: PersonalSettingsInterface): Observable<string> {
    const url = this.platformService
      .getApiEndpointFor('deleteCustomerAccountSettings')
      .replace('%settingId%', personalSettings.id);
    return this.client.delete<string>(url, { headers: this.authHeaders });
  }

  createUserContact(contact: ContactInterface, token: CustomerToken): Observable<ContactInterface> {
    const url = this.platformService.getApiEndpointFor('createContactForUserApi');
    return this.client
      .post<ContactInterface>(
        url,
        {
          firstname: contact.firstname,
          lastname: contact.lastname,
          birthdate: contact.birthdate,
          weight: contact.weight,
          height: contact.height,
          shoeSize: contact.shoeSize,
          skill: contact.skill,
          authUser: `/api/customer-account/users/${token.id}`
        },
        { headers: this.authHeaders }
      )
      .pipe(
        map(response => {
          response.birthdayDate = new Date(response.birthdate);
          return response;
        })
      );
  }

  /**
   * sends a full cancellation request for the given booking.
   * On success, it will update the bookings information, else it will throw an error
   * @param booking
   */
  fullCancellation(booking: BookingsInterface): Observable<number> {
    const url = this.platformService
      .getApiEndpointFor('putFullCancellation')
      .replace('%globalId%', booking.globalId.toString());
    return this.client.put<string>(url, null, { headers: this.authHeaders, observe: 'response' }).pipe(
      map(response => {
        return response.status;
      })
    );
  }

  updateUserContact(contact: ContactInterface, token: CustomerToken): Observable<ContactInterface> {
    const url = this.platformService
      .getApiEndpointFor('updateContactForUserApi')
      .replace('%contactId%', contact.id.toString());
    return this.client
      .put<ContactInterface>(
        url,
        {
          firstname: contact.firstname,
          lastname: contact.lastname,
          birthdate: contact.birthdate,
          weight: contact.weight,
          height: contact.height,
          shoeSize: contact.shoeSize,
          skill: contact.skill,
          authUser: `/api/customer-account/users/${token.id}`
        },
        { headers: this.authHeaders }
      )
      .pipe(
        map(response => {
          response.birthdayDate = new Date(response.birthdate);
          return response;
        })
      );
  }

  deleteUserContact(contact: ContactInterface): Observable<string> {
    const url = this.platformService
      .getApiEndpointFor('deleteContactForUserApi')
      .replace('%contactId%', contact.id.toString());
    return this.client.delete<string>(url, { headers: this.authHeaders });
  }

  deleteUserAccount(): Observable<string> {
    const url = this.platformService.getApiEndpointFor('deleteUserAccountApi');
    return this.client.delete<string>(url, { headers: this.authHeaders });
  }
}
